import React, { useState, useEffect } from 'react';
import { Table, Button, Select, DatePicker, Switch, Input, Space, Row, Col, Typography, message, Card, InputNumber, Tag, Modal, Empty, Popconfirm, Tooltip } from 'antd';
import { DownloadOutlined, SaveOutlined, PlusOutlined, FilePdfOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { 
  fetchClusterAttendance, 
  saveClusterAttendance, 
  deleteClusterAttendance,
  exportClusterAttendanceCSV,
  fetchAttendanceReasons,
  addAttendanceReason,
  deleteAttendanceReason
} from '../../../../Service/Clusters_API/attendanceApi';
import { fetchClusterStandards } from '../../../../Service/Clusters_API/clustersApi';
import { fetchStandards } from '../../../../Service/Syllabus_API/syllabusApi';

const { Text, Title } = Typography;
const { Option } = Select;

const ClusterAttendanceTab = ({ userRole, clusters, loadingClusters, academicYearId }) => {
  // Main view filters
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(userRole === 'STUDIO' ? (clusters && clusters[0]?.cluster_id) : (userRole === 'ADMIN' ? 'all' : null));
  const [selectedStandardFilter, setSelectedStandardFilter] = useState('all');
  const [standards, setStandards] = useState([]);
  const [standardsLoading, setStandardsLoading] = useState(false);

  // Popup Modal states
  const [isEntryModalOpen, setIsEntryModalOpen] = useState(false);
  const [entryDate, setEntryDate] = useState(dayjs());
  const [entryCluster, setEntryCluster] = useState(null);
  const [entryStandards, setEntryStandards] = useState([]);
  const [entryStandardsLoading, setEntryStandardsLoading] = useState(false);
  const [entryStandard, setEntryStandard] = useState(null);
  
  const [institutions, setInstitutions] = useState([]);
  const [isClassCanceled, setIsClassCanceled] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [loadingAttendanceData, setLoadingAttendanceData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasExistingAttendance, setHasExistingAttendance] = useState(false);
  
  // Dynamic Reasons state
  const [reasons, setReasons] = useState([]);
  const [newReason, setNewReason] = useState('');

  // History Matrix states (one per standard)
  const [historyMap, setHistoryMap] = useState({}); // standard_id -> { dates: [], data: [] }
  const [historyLoadingMap, setHistoryLoadingMap] = useState({}); // standard_id -> boolean

  // Monthly Export state
  const [exportMonth, setExportMonth] = useState(dayjs());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadReasons();
  }, []);

  // Auto-default cluster for STUDIO role
  useEffect(() => {
    if (userRole === 'STUDIO' && clusters && clusters.length > 0) {
      if (!selectedCluster || selectedCluster === 'all' || !clusters.some(c => c.cluster_id === selectedCluster)) {
        setSelectedCluster(clusters[0].cluster_id);
      }
    }
  }, [userRole, clusters, selectedCluster]);

  // Fetch standards for Main Page (matrix rendering)
  useEffect(() => {
    if (selectedCluster) {
      loadClusterStandards(selectedCluster);
    } else {
      setStandards([]);
      setHistoryMap({});
      setHistoryLoadingMap({});
    }
    setSelectedStandardFilter('all');
  }, [selectedCluster, academicYearId]);

  // Load history matrices for Main Page
  useEffect(() => {
    if (selectedCluster && standards.length > 0) {
      loadAllHistory(selectedCluster, standards);
    }
  }, [selectedCluster, standards, selectedDate, selectedDateRange]);

  // Load standards for Entry Modal when entryCluster changes
  useEffect(() => {
    if (entryCluster) {
      loadEntryStandards(entryCluster);
    } else {
      setEntryStandards([]);
      setEntryStandard(null);
      setInstitutions([]);
    }
  }, [entryCluster, academicYearId]);

  // Load attendance data for Entry Modal
  useEffect(() => {
    if (isEntryModalOpen && entryCluster && entryStandard && entryDate) {
      loadAttendanceForEntry();
    } else {
      setInstitutions([]);
      setHasExistingAttendance(false);
    }
  }, [isEntryModalOpen, entryCluster, entryStandard, entryDate]);

  const loadReasons = async () => {
    try {
      const res = await fetchAttendanceReasons();
      setReasons(res);
    } catch (error) {
      console.error('Failed to load reasons', error);
    }
  };

  const loadClusterStandards = async (clusterId) => {
    setStandardsLoading(true);
    try {
      let res;
      if (clusterId === 'all') {
        res = await fetchStandards(academicYearId);
      } else {
        res = await fetchClusterStandards(clusterId, academicYearId);
      }
      const sortedList = (res || []).sort((a, b) => {
        const getNum = (str) => {
          const match = String(str || '').match(/(\d+)/);
          return match ? parseInt(match[1], 10) : null;
        };
        const numA = getNum(a.standard_name);
        const numB = getNum(b.standard_name);
        if (numA !== null && numB !== null && numA !== numB) {
          return numA - numB;
        }
        return String(a.standard_name || '').localeCompare(String(b.standard_name || ''), undefined, { numeric: true, sensitivity: 'base' });
      });
      setStandards(sortedList);
    } catch (error) {
      message.error('Failed to load standards');
      setStandards([]);
    } finally {
      setStandardsLoading(false);
    }
  };

  const loadEntryStandards = async (clusterId) => {
    setEntryStandardsLoading(true);
    try {
      const res = await fetchClusterStandards(clusterId, academicYearId);
      const list = Array.isArray(res) ? res.filter(s => s && s.standard_id) : [];
      setEntryStandards(list);
      if (list.length > 0 && list[0]?.standard_id) {
        setEntryStandard(list[0].standard_id);
      } else {
        setEntryStandard(null);
      }
    } catch (error) {
      message.error('Failed to load standards for entry');
      setEntryStandards([]);
      setEntryStandard(null);
    } finally {
      setEntryStandardsLoading(false);
    }
  };

  const loadAttendanceForEntry = async () => {
    setLoadingAttendanceData(true);
    try {
      const dateStr = entryDate.format('YYYY-MM-DD');
      const res = await fetchClusterAttendance(entryCluster, entryStandard, dateStr, academicYearId);
      if (res && res.institutions) {
        setInstitutions(res.institutions);
        setIsClassCanceled(res.is_class_canceled || false);
        setCancelReason(res.cancel_reason || '');
        setHasExistingAttendance(Boolean(res.cluster_attendance_id || res.has_attendance));
      } else {
        setInstitutions([]);
        setIsClassCanceled(false);
        setCancelReason('');
        setHasExistingAttendance(false);
      }
    } catch (error) {
      message.error('Failed to load attendance data');
      setHasExistingAttendance(false);
    } finally {
      setLoadingAttendanceData(false);
    }
  };

  const loadAllHistory = async (clusterId, standardList) => {
    const monthStr = selectedDate ? selectedDate.format('YYYY-MM') : null;
    const startDateStr = (selectedDateRange && selectedDateRange[0]) ? selectedDateRange[0].format('YYYY-MM-DD') : null;
    const endDateStr = (selectedDateRange && selectedDateRange[1]) ? selectedDateRange[1].format('YYYY-MM-DD') : null;
    const validStds = (standardList || []).filter(s => s && s.standard_id);
    const newLoadingMap = {};
    validStds.forEach(std => {
      newLoadingMap[std.standard_id] = true;
    });
    setHistoryLoadingMap(newLoadingMap);

    const fetches = validStds.map(async (std) => {
      try {
        const res = await exportClusterAttendanceCSV(clusterId, std.standard_id, monthStr, academicYearId, startDateStr, endDateStr);
        return {
          standard_id: std.standard_id,
          dates: res?.dates || [],
          data: res?.data || []
        };
      } catch (err) {
        console.error(`Failed to load history for standard ${std.standard_id}`, err);
        return { standard_id: std.standard_id, dates: [], data: [] };
      }
    });

    const results = await Promise.all(fetches);
    const newHistoryMap = {};
    const finalLoadingMap = {};
    results.forEach(res => {
      if (res && res.standard_id) {
        newHistoryMap[res.standard_id] = { dates: res.dates, data: res.data };
        finalLoadingMap[res.standard_id] = false;
      }
    });

    setHistoryMap(newHistoryMap);
    setHistoryLoadingMap(finalLoadingMap);
  };

  const handleSave = async () => {
    if (!entryCluster) return message.warning('Please select a cluster first');
    if (!entryStandard) return message.warning('Please select a standard first');
    setSaving(true);
    try {
      const dateStr = entryDate.format('YYYY-MM-DD');
      await saveClusterAttendance(entryCluster, {
        standard_id: entryStandard,
        attendance_date: dateStr,
        is_class_canceled: isClassCanceled,
        cancel_reason: isClassCanceled ? cancelReason : null,
        institutions: institutions
      });
      message.success('Attendance saved successfully');
      setIsEntryModalOpen(false);
      // Reload history if the saved cluster is either currently selected or we are viewing 'all'
      if (selectedCluster === 'all' || selectedCluster === entryCluster) {
        loadAllHistory(selectedCluster, standards);
      }
    } catch (error) {
      message.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttendance = async () => {
    if (!entryCluster) return message.warning('Please select a cluster first');
    if (!entryStandard) return message.warning('Please select a standard first');
    setDeleting(true);
    try {
      const dateStr = entryDate.format('YYYY-MM-DD');
      await deleteClusterAttendance(entryCluster, entryStandard, dateStr);
      message.success('Attendance record deleted successfully');
      setIsEntryModalOpen(false);
      if (selectedCluster === 'all' || selectedCluster === entryCluster) {
        loadAllHistory(selectedCluster, standards);
      }
    } catch (error) {
      message.error(error?.message || 'Failed to delete attendance');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteFromMatrix = async (clusterId, standardId, dateStr) => {
    try {
      await deleteClusterAttendance(clusterId, standardId, dateStr);
      message.success(`Attendance for ${dayjs(dateStr).format('DD-MM-YYYY')} deleted successfully`);
      if (selectedCluster && standards.length > 0) {
        loadAllHistory(selectedCluster, standards);
      }
    } catch (error) {
      message.error(error?.message || 'Failed to delete attendance');
    }
  };

  const handleExport = async () => {
    if (!selectedCluster) return message.warning('Please select a cluster first');
    setExporting(true);
    try {
      const monthStr = exportMonth ? exportMonth.format('YYYY-MM') : null;
      const startDateStr = (selectedDateRange && selectedDateRange[0]) ? selectedDateRange[0].format('YYYY-MM-DD') : null;
      const endDateStr = (selectedDateRange && selectedDateRange[1]) ? selectedDateRange[1].format('YYYY-MM-DD') : null;
      // Pass null to standard parameter to retrieve all standards combined from the backend
      const res = await exportClusterAttendanceCSV(selectedCluster, null, monthStr, academicYearId, startDateStr, endDateStr);
      
      const dates = res.dates || [];
      if (dates.length === 0) {
        message.warning('No attendance data available for the selected period.');
        return;
      }

      // Group rows by cluster_name and standard_name
      const rawGroups = {};
      (res.data || []).forEach(row => {
        const key = `${row.cluster_name} ${row.standard_name}`.toUpperCase();
        if (!rawGroups[key]) rawGroups[key] = [];
        rawGroups[key].push(row);
      });

      // Keep only groups that have attendance on at least one date
      const activeGroupKeys = Object.keys(rawGroups).filter(key => {
        const rows = rawGroups[key];
        return dates.some(d => rows.some(r => r[d] !== undefined && r[d] !== null && r[d] !== '' && r[d] !== '-'));
      });

      if (activeGroupKeys.length === 0) {
        message.warning('No attendance data available for the selected period.');
        return;
      }

      // Build Excel Spreadsheet HTML content
      let excelContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
          <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>Cluster Attendance</x:Name>
                  <x:WorksheetOptions>
                    <x:DisplayGridlines/>
                  </x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
          <![endif]-->
          <style>
            table { border-collapse: collapse; font-family: Calibri, Arial, sans-serif; font-size: 10pt; }
            th, td { border: 0.5pt solid #d9d9d9; padding: 5px 8px; text-align: center; vertical-align: middle; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .banner-cell { background-color: #002140; color: #ffffff; font-weight: bold; font-size: 11pt; text-align: left; padding: 8px 10px; }
            .left-cell { text-align: left; }
            .tag-p { color: #389e0d; font-weight: bold; }
            .tag-ab { color: #d48806; font-weight: bold; }
            .tag-nc { color: #cf1322; font-weight: bold; }
            .cancel-cell {
              mso-rotate: 90;
              layout-flow: vertical-ideographic;
              writing-mode: tb-rl;
              vertical-align: middle;
              text-align: center;
              background-color: #fff2f0;
              color: #cf1322;
              font-weight: bold;
              font-size: 10pt;
              padding: 4px;
            }
          </style>
        </head>
        <body>
      `;

      activeGroupKeys.forEach(groupKey => {
        const rows = rawGroups[groupKey];
        const groupDates = dates.filter(d => rows.some(r => r[d] !== undefined && r[d] !== null && r[d] !== '' && r[d] !== '-'));
        if (groupDates.length === 0) return;

        excelContent += `
          <table style="margin-bottom: 20px;">
            <thead>
              <tr>
                <th colspan="${groupDates.length + 2}" class="banner-cell">${groupKey}</th>
              </tr>
              <tr>
                <th style="width: 80px;">S-CODE</th>
                <th style="width: 250px;" class="left-cell">SCHOOLNAME</th>
                <th style="width: 100px;">CANDIDATE STRENGTH</th>
        `;

        groupDates.forEach(d => {
          excelContent += `<th>${dayjs(d).format('DD-MM-YYYY')}</th>`;
        });

        excelContent += `
              </tr>
              <tr>
                <th></th>
                <th class="left-cell"></th>
                <th></th>
        `;

        groupDates.forEach(d => {
          excelContent += `<th style="color: #1890ff; font-size: 9pt;">${dayjs(d).format('dddd').toUpperCase()}</th>`;
        });

        excelContent += `
              </tr>
            </thead>
            <tbody>
        `;

        rows.forEach((row, rowIndex) => {
          excelContent += `
            <tr>
              <td><strong>${row.institution_code || row.code || ''}</strong></td>
              <td class="left-cell">${row.institution_name || row.name || ''}</td>
              <td>${row.candidate_strength !== undefined && row.candidate_strength !== null ? row.candidate_strength : 0}</td>
          `;

          groupDates.forEach(d => {
            const val = row[d];
            const isCanceled = typeof val === 'string' && val.startsWith('CANCELED:');

            if (isCanceled) {
              if (rowIndex === 0) {
                const reason = val.replace('CANCELED:', '').trim() || 'Class Canceled';
                excelContent += `<td rowspan="${rows.length}" class="cancel-cell">${reason}</td>`;
              }
              return;
            }

            let cellHtml = '-';
            if (val === 'P' || (!isNaN(val) && val !== '' && Number(val) > 0)) {
              cellHtml = `<span class="tag-p">${val}</span>`;
            } else if (typeof val === 'string' && (val === 'AB' || val.startsWith('AB:') || val.startsWith('AB ('))) {
              const reason = val.includes(':') ? val.split(':').slice(1).join(':').trim() : (val.includes('(') ? val.replace(/^AB\s*\(/i, '').replace(/\)$/, '').trim() : '');
              cellHtml = `<span class="tag-ab">${reason ? `AB: ${reason}` : 'AB'}</span>`;
            } else if (val === 'NO CLASS' || val === 'NC') {
              cellHtml = `<span class="tag-nc">NC</span>`;
            } else if (val === 'VOD') {
              cellHtml = `VOD`;
            } else if (val) {
              cellHtml = val;
            }
            excelContent += `<td>${cellHtml}</td>`;
          });

          excelContent += `</tr>`;
        });

        excelContent += `
            </tbody>
          </table>
          <br/>
        `;
      });

      excelContent += `
        </body>
        </html>
      `;

      // Download as Excel .xls
      const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const encodedUri = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      let fileSuffix = 'Full_Year';
      if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) {
        fileSuffix = `${selectedDateRange[0].format('YYYY_MM_DD')}_to_${selectedDateRange[1].format('YYYY_MM_DD')}`;
      } else if (monthStr) {
        fileSuffix = monthStr;
      }
      link.setAttribute('download', `Cluster_Attendance_${fileSuffix}.xls`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      message.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!selectedCluster) return message.warning('Please select a cluster first');
    setExporting(true);
    try {
      const monthStr = exportMonth ? exportMonth.format('YYYY-MM') : null;
      const startDateStr = (selectedDateRange && selectedDateRange[0]) ? selectedDateRange[0].format('YYYY-MM-DD') : null;
      const endDateStr = (selectedDateRange && selectedDateRange[1]) ? selectedDateRange[1].format('YYYY-MM-DD') : null;

      const displayMonth = selectedDateRange && selectedDateRange[0] && selectedDateRange[1]
        ? `${selectedDateRange[0].format('DD-MM-YYYY')} TO ${selectedDateRange[1].format('DD-MM-YYYY')}`
        : (exportMonth ? exportMonth.format('MMMM YYYY') : (selectedDate ? selectedDate.format('MMMM YYYY') : 'FULL YEAR'));

      const res = await exportClusterAttendanceCSV(selectedCluster, null, monthStr, academicYearId, startDateStr, endDateStr);

      const dates = res.dates || [];
      let rowsData = res.data || [];

      if (dates.length === 0 || rowsData.length === 0) {
        message.warning('No attendance data available for the selected period.');
        return;
      }

      // Filter rowsData if a specific standard filter is selected
      if (selectedStandardFilter && selectedStandardFilter !== 'all') {
        const selStdObj = (standards || []).find(s => s && s.standard_id === selectedStandardFilter);
        if (selStdObj && selStdObj.standard_name) {
          rowsData = rowsData.filter(r => r && (r.standard_name === selStdObj.standard_name || r.standard_id === selectedStandardFilter));
        }
      }

      if (rowsData.length === 0) {
        message.warning('No attendance data found for the selected standard.');
        return;
      }

      const clusterObj = (clusters || []).find(c => c && c.cluster_id === selectedCluster);
      const clusterTitle = selectedCluster === 'all' ? 'All Clusters' : (clusterObj ? clusterObj.cluster_name : `Cluster #${selectedCluster}`);

      // Group rows by cluster_name and standard_name
      const rawGroups = {};
      rowsData.forEach(row => {
        const key = `${row.cluster_name || 'Cluster'} - ${row.standard_name || 'Standard'}`;
        if (!rawGroups[key]) rawGroups[key] = [];
        rawGroups[key].push(row);
      });

      // Filter groups that have attendance on at least one date
      const activeGroupKeys = Object.keys(rawGroups).filter(key => {
        const rows = rawGroups[key];
        return dates.some(d => rows.some(r => r[d] !== undefined && r[d] !== null && r[d] !== '' && r[d] !== '-'));
      });

      if (activeGroupKeys.length === 0) {
        message.warning('No attendance data available for the selected period.');
        return;
      }

      let printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Cluster Attendance Matrix - ${clusterTitle} (${displayMonth})</title>
          <style>
            @page { size: A4 landscape; margin: 8mm; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #1f2937; margin: 0; padding: 12px; background: #fff; }
            .group-page {
              page-break-after: always;
              break-after: page;
            }
            .group-page:last-child {
              page-break-after: avoid;
              break-after: avoid;
            }
            .report-container {
              width: 100%;
              border-collapse: collapse;
              border: none !important;
              margin-bottom: 20px;
            }
            .report-container > thead > tr > td,
            .report-container > tbody > tr > td {
              border: none !important;
              padding: 0 !important;
            }
            thead {
              display: table-header-group;
            }
            .header-box { text-align: center; border-bottom: 2px solid #1890ff; padding-bottom: 8px; margin-bottom: 12px; }
            .header-title { font-size: 18px; font-weight: bold; color: #002140; margin: 0 0 4px 0; text-transform: uppercase; letter-spacing: 0.5px; }
            .header-sub { font-size: 12px; color: #1890ff; font-weight: 600; text-transform: uppercase; }
            .meta-info { display: flex; justify-content: space-between; font-size: 11px; color: #374151; margin-bottom: 16px; border: 1px solid #d9d9d9; padding: 8px 14px; border-radius: 6px; background: #fafafa; }
            .meta-item { font-weight: 600; }
            .card-header { font-size: 13px; font-weight: bold; color: #002140; background: #f0f5ff; border: 1px solid #adc6ff; padding: 6px 12px; margin-bottom: 8px; border-radius: 6px 6px 0 0; }
            table.data-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10px; page-break-inside: avoid; border: 1px solid #d9d9d9; }
            th, td { border: 1px solid #d9d9d9; padding: 5px 6px; text-align: center; }
            th { background-color: #fafafa; color: rgba(0, 0, 0, 0.88); font-weight: bold; font-size: 10px; vertical-align: middle; }
            td.left { text-align: left; }
            .tag-p { background: #f6ffed; border: 1px solid #b7eb8f; color: #389e0d; font-weight: 600; padding: 1px 6px; border-radius: 4px; font-size: 10px; display: inline-block; }
            .tag-ab { background: #fffbe6; border: 1px solid #ffe58f; color: #d48806; font-weight: 600; padding: 1px 6px; border-radius: 4px; font-size: 10px; display: inline-block; }
            .tag-nc { background: #fff2f0; border: 1px solid #ffccc7; color: #cf1322; font-weight: 600; padding: 1px 6px; border-radius: 4px; font-size: 10px; display: inline-block; }
            .tag-vod { background: #fafafa; border: 1px solid #d9d9d9; color: rgba(0, 0, 0, 0.88); font-weight: 600; padding: 1px 6px; border-radius: 4px; font-size: 10px; display: inline-block; }
            .tag-cancel { 
              background: #fff2f0; 
              border: 1px solid #ffccc7; 
              color: #cf1322; 
              font-weight: bold; 
              padding: 8px 4px; 
              border-radius: 4px; 
              font-size: 10px; 
              display: inline-block; 
              text-align: center;
              writing-mode: vertical-rl;
              transform: rotate(180deg);
              white-space: nowrap;
              letter-spacing: 1px;
              text-transform: uppercase;
            }
            .cancel-cell {
              background: #fff2f0 !important;
              vertical-align: middle !important;
              text-align: center !important;
              padding: 2px !important;
            }
            .footer-text { text-align: right; font-size: 9px; color: #8c8c8c; margin-top: 20px; border-top: 1px solid #e8e8e8; padding-top: 6px; }
          </style>
        </head>
        <body>
      `;

      activeGroupKeys.forEach((groupKey, gIdx) => {
        const groupRows = rawGroups[groupKey];
        const groupDates = dates.filter(d => groupRows.some(r => r[d] !== undefined && r[d] !== null && r[d] !== '' && r[d] !== '-'));
        if (groupDates.length === 0) return;

        const groupClusterName = groupRows[0]?.cluster_name || clusterTitle;

        printContent += `
          <div class="group-page">
            <table class="report-container">
              <thead>
                <tr>
                  <td>
                    <div class="header-box">
                      <h1 class="header-title">VEDIKA PORTAL - CLUSTER ATTENDANCE REGISTER</h1>
                      <div class="header-sub">${(selectedDateRange ? 'PERIOD ATTENDANCE MATRIX REPORT' : 'MONTHLY ATTENDANCE MATRIX REPORT')} (${displayMonth.toUpperCase()})</div>
                    </div>

                    <div class="meta-info">
                      <div class="meta-item">CLUSTER: <span style="color: #1890ff;">${groupClusterName}</span></div>
                      <div class="meta-item">${(selectedDateRange ? 'PERIOD' : 'MONTH / YEAR')}: <span style="color: #1890ff;">${displayMonth}</span></div>
                      <div class="meta-item">GENERATED ON: <span style="color: #595959;">${dayjs().format('DD-MM-YYYY HH:mm')}</span></div>
                    </div>
                  </td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div class="card-header">${groupKey}</div>
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th style="width: 55px;">Code</th>
                          <th style="text-align: left; width: 170px;">Institution Name</th>
                          <th style="width: 70px;">Candidate Strength</th>
        `;

        groupDates.forEach(d => {
          printContent += `
            <th>
              <div style="font-weight: bold; color: rgba(0, 0, 0, 0.88);">${dayjs(d).format('DD-MM-YYYY')}</div>
              <div style="font-size: 8px; color: #1890ff; font-weight: bold; margin-top: 2px;">${dayjs(d).format('dddd').toUpperCase()}</div>
            </th>
          `;
        });

        printContent += `
                        </tr>
                      </thead>
                      <tbody>
        `;

        groupRows.forEach((row, rowIndex) => {
          printContent += `
            <tr>
              <td><strong>${row.institution_code || row.code || ''}</strong></td>
              <td class="left">${row.institution_name || row.name || ''}</td>
              <td><strong>${row.candidate_strength !== undefined && row.candidate_strength !== null ? row.candidate_strength : 0}</strong></td>
          `;

          groupDates.forEach(d => {
            const val = row[d];
            const isCanceled = typeof val === 'string' && val.startsWith('CANCELED:');
            
            if (isCanceled) {
              if (rowIndex === 0) {
                const reason = val.replace('CANCELED:', '').trim() || 'Class Canceled';
                printContent += `
                  <td rowspan="${groupRows.length}" class="cancel-cell">
                    <div style="display: flex; align-items: center; justify-content: center; height: 100%; min-height: 80px;">
                      <span class="tag-cancel">${reason}</span>
                    </div>
                  </td>
                `;
              }
              return;
            }

            let cellHtml = '-';
            if (val === 'P' || (!isNaN(val) && val !== '' && Number(val) > 0)) {
              cellHtml = `<span class="tag-p">${val}</span>`;
            } else if (typeof val === 'string' && (val === 'AB' || val.startsWith('AB:') || val.startsWith('AB ('))) {
              const reason = val.includes(':') ? val.split(':').slice(1).join(':').trim() : (val.includes('(') ? val.replace(/^AB\s*\(/i, '').replace(/\)$/, '').trim() : '');
              const displayText = reason ? `AB: ${reason}` : 'AB';
              cellHtml = `<span class="tag-ab" style="white-space: normal; height: auto; padding: 2px 4px; font-size: 9px; line-height: 1.1;">${displayText}</span>`;
            } else if (val === 'NO CLASS' || val === 'NC') {
              cellHtml = `<span class="tag-nc">NC</span>`;
            } else if (val === 'VOD') {
              cellHtml = `<span class="tag-vod">VOD</span>`;
            } else if (val) {
              cellHtml = val;
            }
            printContent += `<td>${cellHtml}</td>`;
          });

          printContent += `</tr>`;
        });

        printContent += `
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        `;
      });

      printContent += `
          <div class="footer-text">Vedika Cluster Attendance Register &copy; ${new Date().getFullYear()} - Official Document</div>
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        message.success('PDF print report generated.');
      } else {
        message.error('Pop-up blocked! Please allow pop-ups to export/print PDF.');
      }
    } catch (err) {
      console.error('PDF export failed:', err);
      message.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleAddReason = async () => {
    if (!newReason.trim()) return;
    try {
      await addAttendanceReason(newReason);
      setNewReason('');
      loadReasons();
      message.success('Reason added');
    } catch (error) {
      message.error('Failed to add reason');
    }
  };

  const handleDeleteReason = async (reasonId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    try {
      await deleteAttendanceReason(reasonId);
      loadReasons();
      message.success('Reason deleted');
    } catch (error) {
      message.error('Failed to delete reason');
    }
  };

  const handleInstitutionChange = (index, field, value) => {
    setInstitutions(prev => prev.map((inst, idx) => {
      if (idx === index) {
        return { ...inst, [field]: value };
      }
      return inst;
    }));
  };

  const entryColumns = [
    {
      title: 'Code',
      dataIndex: 'institution_code',
      key: 'institution_code',
      width: '10%',
      render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Institution Name',
      dataIndex: 'institution_name',
      key: 'institution_name',
      width: '25%',
      render: (text, record) => (
        <span style={{ fontWeight: record.is_vod ? 'bold' : 'normal', color: record.is_vod ? 'var(--primary-color)' : 'inherit' }}>
          {text} {record.is_vod ? '(VOD)' : ''}
        </span>
      )
    },
    {
      title: 'Candidate Strength',
      dataIndex: 'candidate_strength',
      key: 'candidate_strength',
      width: '10%',
      align: 'center',
      render: (val) => (
        <Tag color="cyan" style={{ fontWeight: 600, fontSize: 12 }}>
          {val !== undefined && val !== null ? val : 0}
        </Tag>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: '15%',
      render: (_, record, index) => (
        <Select showSearch optionFilterProp="children" 
          value={record.status} 
          onChange={(val) => handleInstitutionChange(index, 'status', val)}
          style={{ width: '100%' }}
          disabled={isClassCanceled}
        >
          <Option value="PRESENT">Present (P)</Option>
          <Option value="ABSENT">Absent (AB)</Option>
          <Option value="VOD">VOD</Option>
        </Select>
      )
    },
    {
      title: 'Student Count',
      key: 'student_count',
      width: '15%',
      render: (_, record, index) => (
        <InputNumber 
          min={0}
          value={record.student_count}
          onChange={(val) => handleInstitutionChange(index, 'student_count', val)}
          disabled={isClassCanceled || record.status !== 'PRESENT' || record.is_vod}
          placeholder={record.is_vod ? 'VOD' : 'Count'}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Absent Reason',
      key: 'absent_reason',
      width: '25%',
      render: (_, record, index) => (
        <Select showSearch
          value={record.absent_reason}
          onChange={(val) => handleInstitutionChange(index, 'absent_reason', val)}
          disabled={isClassCanceled || record.status !== 'ABSENT'}
          style={{ width: '100%' }}
          placeholder="Select Reason"
          allowClear
          filterOption={(input, option) =>
            (option?.value || '').toLowerCase().includes(input.toLowerCase())
          }
          dropdownRender={userRole === 'ADMIN' ? (menu => (
            <>
              {menu}
              <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8, borderTop: '1px solid var(--border-color)' }}>
                <Input style={{ flex: 'auto' }} value={newReason} onChange={e => setNewReason(e.target.value)} placeholder="New Reason" />
                <Button type="link" onClick={handleAddReason} icon={<PlusOutlined />}>Add</Button>
              </div>
            </>
          )) : undefined}
        >
          {reasons.map(r => (
            <Option key={r.reason_id} value={r.reason_text}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>{r.reason_text}</span>
                {userRole === 'ADMIN' && (
                  <Button 
                    type="text" 
                    size="small" 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={(e) => handleDeleteReason(r.reason_id, e)}
                    style={{ height: 'auto', padding: '0 4px' }}
                  />
                )}
              </div>
            </Option>
          ))}
        </Select>
      )
    }
  ];

  // Cell and Header Compact Styles
  const cellStyle = { padding: '4px 6px', fontSize: '11px', height: '32px' };
  const headerCellStyle = { padding: '6px 4px', fontSize: '11px' };

  return (
    <div style={{ padding: 16 }}>
      {/* Selection Header Row */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24, gap: 16 }}>
        <Col>
          <Space size="middle" style={{ display: 'flex', flexWrap: 'wrap' }}>
            <Select showSearch optionFilterProp="children" 
              style={{ width: 220 }} 
              placeholder="Select a cluster"
              value={selectedCluster}
              onChange={(val) => {
                setSelectedCluster(val);
              }}
              loading={loadingClusters}
            >
              {userRole !== 'STUDIO' && <Option value="all">All Clusters</Option>}
              {clusters.map(c => (
                <Option key={c.cluster_id} value={c.cluster_id}>{c.cluster_name}</Option>
              ))}
            </Select>
            <Select showSearch optionFilterProp="children"
              style={{ width: 200 }}
              placeholder="All Standards"
              value={selectedStandardFilter}
              onChange={setSelectedStandardFilter}
              disabled={!selectedCluster}
            >
              <Option value="all">All Standards</Option>
              {standards.map(s => {
                const clusterObj = clusters.find(c => c.cluster_id === selectedCluster);
                const clusterName = clusterObj && selectedCluster !== 'all' ? clusterObj.cluster_name : '';
                return (
                  <Option key={s.standard_id} value={s.standard_id}>
                    {clusterName ? `${clusterName} - ` : ''}{s.standard_name}
                  </Option>
                );
              })}
            </Select>
            <DatePicker 
              picker="month" 
              value={selectedDate} 
              onChange={(date) => {
                if (date) {
                  setSelectedDate(date);
                  setExportMonth(date);
                  setSelectedDateRange(null);
                }
              }} 
              allowClear={true}
              style={{ width: 140 }}
              placeholder="Select Month"
            />
            <DatePicker.RangePicker 
              value={selectedDateRange}
              onChange={(dates) => {
                setSelectedDateRange(dates);
                if (dates) {
                  setSelectedDate(null);
                  setExportMonth(null);
                }
              }} 
              allowClear={true}
              style={{ width: 220 }}
              placeholder={['Start Date', 'End Date']}
            />
          </Space>
        </Col>
        
        <Col>
          <Space size="middle" style={{ display: 'flex', flexWrap: 'wrap' }}>
            <Button type="dashed" icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
              Export Excel
            </Button>
            <Button type="primary" danger icon={<FilePdfOutlined />} onClick={handleExportPDF} loading={exporting}>
              Export PDF
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => {
                // Initialize entry Modal states with currently selected cluster if available (excluding 'all')
                setEntryCluster(selectedCluster && selectedCluster !== 'all' ? selectedCluster : (userRole === 'STUDIO' && clusters && clusters.length > 0 ? clusters[0].cluster_id : null));
                setEntryDate(dayjs());
                setIsEntryModalOpen(true);
              }}
            >
              Take / Edit Attendance
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Main Page: Display Monthly Attendance Matrix for each Standard */}
      {selectedCluster ? (
        standards.length > 0 ? (
          <div>
            <Title level={4} style={{ marginBottom: 20 }}>
              Monthly Attendance Matrices ({selectedDateRange && selectedDateRange[0] && selectedDateRange[1]
                ? `${selectedDateRange[0].format('DD-MM-YYYY')} TO ${selectedDateRange[1].format('DD-MM-YYYY')}`
                : (selectedDate ? selectedDate.format('MMMM YYYY') : 'Full Year')})
            </Title>
            {(() => {
              const anyLoading = Object.values(historyLoadingMap).some(Boolean) || standardsLoading;
              const renderedCards = [];

              (standards || [])
                .filter(std => std && std.standard_id && (selectedStandardFilter === 'all' || std.standard_id === selectedStandardFilter))
                .forEach(std => {
                  const hist = historyMap[std.standard_id] || { dates: [], data: [] };
                  const isHistLoading = historyLoadingMap[std.standard_id] || false;
                  
                  // Skip standards that have no linked institutions or no dates
                  if ((hist.data.length === 0 || hist.dates.length === 0) && !isHistLoading) {
                    return;
                  }

                  // Group data by cluster_name
                  const groupedData = {};
                  hist.data.forEach(row => {
                    const cName = row.cluster_name || 'No Cluster';
                    if (!groupedData[cName]) groupedData[cName] = [];
                    groupedData[cName].push(row);
                  });

                  Object.keys(groupedData).forEach(cName => {
                    const tableRows = groupedData[cName] || [];

                    // Filter dates where this specific cluster has attendance records
                    const clusterDates = hist.dates.filter(dateStr => {
                      return tableRows.some(row => {
                        const val = row[dateStr];
                        return val !== undefined && val !== null && val !== '' && val !== '-';
                      });
                    });

                    // If this cluster has NO attendance recorded in any date, do NOT show this cluster's matrix
                    if (clusterDates.length === 0 && !isHistLoading) {
                      return;
                    }

                    // Prepare columns dynamically using only dates with records for this cluster
                    const histColumns = [
                      {
                        title: 'Code',
                        dataIndex: 'code',
                        key: 'code',
                        width: 55,
                        fixed: 'left',
                        onCell: () => ({ style: cellStyle }),
                        onHeaderCell: () => ({ style: headerCellStyle }),
                        render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>
                      },
                      {
                        title: 'Institution Name',
                        dataIndex: 'name',
                        key: 'name',
                        width: 170,
                        fixed: 'left',
                        onCell: () => ({ style: cellStyle }),
                        onHeaderCell: () => ({ style: headerCellStyle }),
                      },
                      {
                        title: 'Candidate Strength',
                        dataIndex: 'candidate_strength',
                        key: 'candidate_strength',
                        width: 85,
                        fixed: 'left',
                        align: 'center',
                        onCell: () => ({ style: cellStyle }),
                        onHeaderCell: () => ({ style: headerCellStyle }),
                        render: (val) => (
                          <Tag color="cyan" style={{ fontWeight: 600, fontSize: '11px', margin: 0 }}>
                            {val !== undefined && val !== null ? val : 0}
                          </Tag>
                        )
                      },
                      ...clusterDates.map(dateStr => {
                        const targetClusterId = tableRows[0]?.cluster_id || (selectedCluster !== 'all' ? selectedCluster : null);
                        return {
                          title: (
                            <div style={{ textAlign: 'center', lineHeight: '1.1' }}>
                              <div style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>{dayjs(dateStr).format('DD-MM-YYYY')}</div>
                              <div style={{ fontSize: '9px', color: '#1890ff', fontWeight: 'bold', marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                <span>{dayjs(dateStr).format('dddd').toUpperCase()}</span>
                                {targetClusterId && (
                                  <Space size={2} style={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <Tooltip title="Edit attendance for this date">
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<EditOutlined style={{ fontSize: '11px', color: '#1890ff' }} />}
                                        style={{ width: 18, height: 18, padding: 0, minWidth: 18 }}
                                        onClick={(e) => {
                                          if (e) e.stopPropagation();
                                          setEntryCluster(targetClusterId);
                                          setEntryStandard(std.standard_id);
                                          setEntryDate(dayjs(dateStr));
                                          setIsEntryModalOpen(true);
                                        }}
                                      />
                                    </Tooltip>
                                    <Popconfirm
                                      title="Delete Attendance"
                                      description={`Delete attendance for ${dayjs(dateStr).format('DD-MM-YYYY')} (${cName} - ${std.standard_name})?`}
                                      onConfirm={(e) => {
                                        if (e) e.stopPropagation();
                                        handleDeleteFromMatrix(targetClusterId, std.standard_id, dateStr);
                                      }}
                                      okText="Yes, Delete"
                                      okButtonProps={{ danger: true }}
                                      cancelText="Cancel"
                                      placement="bottom"
                                    >
                                      <Tooltip title="Delete attendance for this date">
                                        <Button
                                          type="text"
                                          size="small"
                                          danger
                                          icon={<DeleteOutlined style={{ fontSize: '11px' }} />}
                                          style={{ width: 18, height: 18, padding: 0, minWidth: 18 }}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </Tooltip>
                                    </Popconfirm>
                                  </Space>
                                )}
                              </div>
                            </div>
                          ),
                          dataIndex: dateStr,
                          key: dateStr,
                          align: 'center',
                          width: 105,
                          onCell: (record) => {
                            const val = record[dateStr];
                            const isCanceled = typeof val === 'string' && val.startsWith('CANCELED:');
                            if (isCanceled) {
                              return {
                                style: {
                                  ...cellStyle,
                                  background: '#fff2f0',
                                  verticalAlign: 'middle',
                                  textAlign: 'center',
                                  padding: '2px'
                                }
                              };
                            }
                            return { style: cellStyle };
                          },
                          onHeaderCell: () => ({ style: headerCellStyle }),
                          render: (text, record, index) => {
                          if (typeof text === 'string' && text.startsWith('CANCELED:')) {
                            const reason = text.replace('CANCELED:', '').trim() || 'Class Canceled';
                            return {
                              children: (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '100%',
                                  minHeight: '90px',
                                  padding: '4px 0'
                                }}>
                                  <span 
                                    style={{ 
                                      writingMode: 'vertical-rl',
                                      transform: 'rotate(180deg)',
                                      whiteSpace: 'nowrap',
                                      color: '#cf1322',
                                      fontWeight: 700,
                                      fontSize: '11px',
                                      letterSpacing: '1.5px',
                                      textTransform: 'uppercase',
                                      background: '#fff2f0',
                                      border: '1px solid #ffccc7',
                                      padding: '10px 4px',
                                      borderRadius: '4px',
                                      display: 'inline-block',
                                      textAlign: 'center',
                                      boxShadow: '0 1px 2px rgba(207, 19, 34, 0.08)'
                                    }}
                                  >
                                    {reason}
                                  </span>
                                </div>
                              ),
                              props: {
                                rowSpan: index === 0 ? tableRows.length : 0
                              }
                            };
                          }
                          if (text === 'NO CLASS') return <Tag color="error" style={{ margin: 0, padding: '0 4px', fontSize: '10px' }}>NC</Tag>;
                          if (typeof text === 'string' && (text === 'AB' || text.startsWith('AB:') || text.startsWith('AB ('))) {
                            const reason = text.includes(':') ? text.split(':').slice(1).join(':').trim() : (text.includes('(') ? text.replace(/^AB\s*\(/i, '').replace(/\)$/, '').trim() : '');
                            return (
                              <Tag 
                                color="warning" 
                                style={{ 
                                  margin: 0, 
                                  padding: '2px 4px', 
                                  fontSize: '10px',
                                  whiteSpace: 'normal',
                                  height: 'auto', 
                                  lineHeight: '1.2'
                                }}
                                title={reason ? `Absent Reason: ${reason}` : 'Absent'}
                              >
                                {reason ? `AB: ${reason}` : 'AB'}
                              </Tag>
                            );
                          }
                          if (text === 'VOD') return <Tag color="default" style={{ margin: 0, padding: '0 4px', fontSize: '10px' }}>VOD</Tag>;
                          if (text === 'P') return <Tag color="success" style={{ margin: 0, padding: '0 4px', fontSize: '10px' }}>P</Tag>;
                          if (!isNaN(text) && text !== '') return <Tag color="success" style={{ margin: 0, padding: '0 4px', fontSize: '10px' }}>{text}</Tag>;
                          return text || '-';
                        }
                      };
                    })
                    ];

                    renderedCards.push(
                      <Card 
                        key={`${std.standard_id}-${cName}`}
                        title={`${cName} - ${std.standard_name}`}
                        style={{ marginBottom: 28, border: '1px solid var(--border-color)', borderRadius: 12 }}
                        bodyStyle={{ padding: 12 }}
                      >
                        <Table
                          columns={histColumns}
                          dataSource={tableRows}
                          rowKey={(record, idx) => record.cluster_institution_id ? `ci_${record.cluster_institution_id}` : (record.code ? `code_${record.code}` : `idx_${idx}`)}
                          loading={isHistLoading}
                          pagination={false}
                          bordered
                          size="small"
                          scroll={{ x: 'max-content' }}
                        />
                      </Card>
                    );
                  });
                });

              if (renderedCards.length > 0) {
                return renderedCards;
              }

              if (anyLoading) {
                return (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">Loading attendance matrices...</Text>
                  </div>
                );
              }

              return (
                <Card style={{ textAlign: 'center', padding: '48px 24px', borderRadius: 12, border: '1px dashed var(--border-color)' }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span style={{ color: 'var(--text-secondary)' }}>
                        No attendance recorded for {selectedCluster === 'all' ? 'any cluster' : 'this cluster'} in {selectedDateRange && selectedDateRange[0] && selectedDateRange[1]
                          ? `${selectedDateRange[0].format('DD-MM-YYYY')} TO ${selectedDateRange[1].format('DD-MM-YYYY')}`
                          : (selectedDate ? selectedDate.format('MMMM YYYY') : 'Full Year')}.
                      </span>
                    }
                  >
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setEntryCluster(selectedCluster && selectedCluster !== 'all' ? selectedCluster : null);
                        setEntryDate(dayjs());
                        setIsEntryModalOpen(true);
                      }}
                    >
                      Take / Edit Attendance
                    </Button>
                  </Empty>
                </Card>
              );
            })()}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>
            No standards linked to this cluster for the active academic year.
          </div>
        )
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
          Please select a cluster to view the attendance matrices.
        </div>
      )}

      {/* Attendance Entry Form Popup Modal */}
      <Modal
        title={
          <div style={{ paddingBottom: 8, borderBottom: '1px solid var(--border-color)' }}>
            <Title level={4} style={{ margin: 0 }}>Take / Edit Attendance</Title>
          </div>
        }
        open={isEntryModalOpen}
        onCancel={() => setIsEntryModalOpen(false)}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <div style={{ padding: '16px 0' }}>
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={24} md={8}>
              <Text strong>Attendance Date:</Text><br />
              <DatePicker 
                value={entryDate} 
                onChange={setEntryDate} 
                allowClear={false}
                style={{ width: '100%', marginTop: 4 }}
              />
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Select Cluster:</Text><br />
              <Select showSearch optionFilterProp="children" 
                style={{ width: '100%', marginTop: 4 }} 
                placeholder="Select Cluster"
                value={entryCluster}
                onChange={(val) => {
                  setEntryCluster(val);
                  setEntryStandard(null);
                }}
                loading={loadingClusters}
              >
                {clusters.map(c => (
                  <Option key={c.cluster_id} value={c.cluster_id}>{c.cluster_name}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={8}>
              <Text strong>Select Standard:</Text><br />
              <Select showSearch optionFilterProp="children" 
                style={{ width: '100%', marginTop: 4 }} 
                placeholder="Select Standard"
                value={entryStandard}
                onChange={setEntryStandard}
                loading={entryStandardsLoading}
                disabled={!entryCluster}
              >
                {entryStandards.map(s => {
                  const clusterObj = clusters.find(c => c.cluster_id === entryCluster);
                  const clusterName = clusterObj ? clusterObj.cluster_name : '';
                  return (
                    <Option key={s.standard_id} value={s.standard_id}>
                      {clusterName ? `${clusterName} - ` : ''}{s.standard_name}
                    </Option>
                  );
                })}
              </Select>
            </Col>
          </Row>

          {/* Cancellation section */}
          {entryCluster && entryStandard && (
            <Card style={{ marginBottom: 20, background: isClassCanceled ? 'rgba(239, 68, 68, 0.12)' : 'var(--panel-color)', border: isClassCanceled ? '1px solid #ef4444' : '1px solid var(--border-color)' }}>
              <Row align="middle" gutter={16}>
                <Col>
                  <Text strong style={{ fontSize: 15, color: isClassCanceled ? '#f87171' : 'var(--text-color)' }}>Cancel Class for Entire Cluster:</Text>
                </Col>
                <Col>
                  <Switch checked={isClassCanceled} onChange={setIsClassCanceled} />
                </Col>
                {isClassCanceled && (
                  <Col flex="auto">
                    <Input 
                      placeholder="Reason for cancellation (e.g. Local Holiday, Heavy Rain)" 
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                    />
                  </Col>
                )}
              </Row>
            </Card>
          )}

          {/* Institutions Entry Table */}
          {entryCluster && entryStandard ? (
            <>
              <Table 
                columns={entryColumns} 
                dataSource={institutions} 
                rowKey={(record, idx) => record.cluster_institution_id ? `ci_${record.cluster_institution_id}` : (record.institution_code ? `code_${record.institution_code}` : `idx_${idx}`)} 
                loading={loadingAttendanceData}
                pagination={false}
                bordered
                size="middle"
                scroll={{ x: 'max-content' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                <div>
                  {hasExistingAttendance && (
                    <Popconfirm
                      title="Delete Attendance Record"
                      description={`Are you sure you want to delete the attendance record for ${entryDate.format('DD-MM-YYYY')}? This action cannot be undone.`}
                      onConfirm={handleDeleteAttendance}
                      okText="Yes, Delete"
                      okButtonProps={{ danger: true, loading: deleting }}
                      cancelText="Cancel"
                    >
                      <Button danger icon={<DeleteOutlined />} loading={deleting}>
                        Delete Attendance
                      </Button>
                    </Popconfirm>
                  )}
                </div>
                <Space>
                  <Button onClick={() => setIsEntryModalOpen(false)}>Cancel</Button>
                  <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                    Save Attendance
                  </Button>
                </Space>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>
              Please select a cluster and standard to enter attendance
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ClusterAttendanceTab;
